/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { api } from '../APIs/api';
import { API_URL, BASE_URL } from '../APIs/endpoint';
import { TravelPlannerSlideData } from '../constants/travelPlannerData';
import { fetchMetaDataList } from '../Redux/slices/metaDataSlice';
import { META_DATA_SLUG } from '../APIs/metaDataSlug';
import { HelmetProvider } from 'react-helmet-async';
import { toast } from 'react-toastify';
import HelmetComponent from '../components/MetaDataComponent/Helmet';
import HomeSliderComponent from '../components/Home/HomeSlider/HomeSlider';
import BusinessCount from '../components/Home/BusinessCount/BusinessCount';
import Adventures from '../components/Home/Adventures/AdventureSection';
import TravelPlanner from '../components/Home/TravelPlanner/TravelPlanner';
import FaqComponent from '../components/Home/Faq/index';
import MustSeeSpots from '../components/Home/MustSeeSpots/MustSeeSpots';
import ExploreIndia from '../components/Home/ExploreIndia/ExploreIndia';
import Header from '../layout/Header/Header';

const MotionBox = motion.create(Box);

const Home = () => {
  const dispatch = useDispatch();
  const homeMetaData = useSelector((state) => state?.metaDataList?.metaDataList[0]);
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [homeSliderData, setHomeSliderData] = useState([]);
  const [businessCount, setBusinessCount] = useState([]);
  const [mustSeeData, setMustSeeData] = useState([]);
  const [whyIndiaData, setWhyIndiaData] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [faqAccordionData, setFaqAccordionData] = useState([]);
  const [themeData, setThemeData] = useState();

  const observer = useRef();
  const mustSeeRef = useRef(null);
  const exploreMapRef = useRef(null);
  const whyIndiaRef = useRef(null);
  const seasonalTripsRef = useRef(null);
  const reviewRef = useRef(null);
  const faqRef = useRef(null);

  const [sectionVisibility, setSectionVisibility] = useState({
    headerBanner: false,
    businessCount: false,
    mustSeeSpots: false,
    exploreIndia: false,
    adventures: false,
    travelPlanner: false,
    faq: false
  });

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setSectionVisibility((prev) => ({
            ...prev,
            [entry.target.id]: true
          }));
          observer.current.unobserve(entry.target);
        }
      });
    };

    observer.current = new IntersectionObserver(handleIntersect, options);
    const sections = [
      'headerBanner',
      'businessCount',
      'mustSeeSpots',
      'exploreIndia',
      'adventures',
      'travelPlanner',
      'faq'
    ];
    sections.forEach((section) => {
      const sectionElement = document.getElementById(section);
      if (sectionElement) {
        observer.current.observe(sectionElement);
      }
    });
    return () => {
      observer.current.disconnect();
    };
  }, []);

  const getHomeSliderImageApi = async () => {
    try {
      setIsLoader(true);
      const homeSliderRes = await api.get(`${BASE_URL}${API_URL.HOME_SLIDER}`);
      const { data, message, status } = homeSliderRes;
      if (status) {
        setHomeSliderData(data);
      } else {
        toast.error(message, { autoClose: 3000 });
      }
    } catch (err) {
      toast.error(err.message, { autoClose: 3000 });
    } finally {
      setIsLoader(false);
    }
  };

  const getBusinessCountsAPi = async () => {
    try {
      setIsLoader(true);
      const businessCountRes = await api.get(`${BASE_URL}${API_URL.BUSINESS_COUNT}`);
      const { data, message, status } = businessCountRes;
      if (status) {
        setBusinessCount(data);
      } else {
        toast.error(message, { autoClose: 3000 });
      }
    } catch (err) {
      toast.error(err.message, { autoClose: 3000 });
    } finally {
      setIsLoader(false);
    }
  };

  const getIndiaMustSeeListApi = async () => {
    try {
      setIsLoader(true);
      const mustSeeRes = await api.get(`${BASE_URL}${API_URL.INDIA_MUST_SEE_LIST}`);
      const { data, message, status } = mustSeeRes;
      if (status) {
        setMustSeeData(data);
      } else {
        toast.error(message, { autoClose: 3000 });
      }
    } catch (err) {
      toast.error(err.message, { autoClose: 3000 });
    } finally {
      setIsLoader(false);
    }
  };

  const getWhyIndiaListApi = async () => {
    try {
      setIsLoader(true);
      const whyIndiaRes = await api.get(`${BASE_URL}${API_URL.WHY_INDIA_LIST}`);
      const { data, message, status } = whyIndiaRes;
      if (status) {
        setWhyIndiaData(data);
      } else {
        toast.error(message, { autoClose: 3000 });
      }
    } catch (err) {
      toast.error(err.message, { autoClose: 3000 });
    } finally {
      setIsLoader(false);
    }
  };

  const getThemeListApi = async () => {
    try {
      setIsLoader(true);
      const themeRes = await api.get(`${BASE_URL}${API_URL.THEME_LIST}`);
      const { data, message, status } = themeRes;
      if (status) {
        setThemeData(data);
      } else {
        toast.error(message, { autoClose: 3000 });
      }
    } catch (err) {
      toast.error(err.message, { autoClose: 3000 });
    } finally {
      setIsLoader(false);
    }
  };

  const getFqaListApi = async () => {
    try {
      setIsLoader(true);
      const faqListRes = await api.get(`${BASE_URL}${API_URL.GET_FAQ_LIST}?destination=${'India'}`);
      const { data, status } = faqListRes;
      if (status && Object.keys(data)?.length > 0) {
        const faqDestination = data?.Destination;
        const firstDestinationKey = Object.keys(faqDestination)[0];
        const QueAnsData = faqDestination[firstDestinationKey];

        if (QueAnsData?.length > 0) {
          const questionsAndAnswers = QueAnsData.map(({ question, answer }) => ({
            question,
            answer
          }));
          setFaqAccordionData(questionsAndAnswers);
        } else {
          setFaqAccordionData([]);
        }
      } else {
        setFaqAccordionData([]);
      }
    } catch (err) {
      toast.error(err.message, { autoClose: 3000 });
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    sessionStorage.setItem('tripDetails', JSON.stringify({}));
    sessionStorage.setItem('currentPage', 1);
    dispatch(fetchMetaDataList({ slug: META_DATA_SLUG.HOME_META_SLUG })).finally(() =>
      setIsLoader(false)
    );
    getHomeSliderImageApi();
    getBusinessCountsAPi();
    getIndiaMustSeeListApi();
    getWhyIndiaListApi();
    getThemeListApi();
    getFqaListApi();
  }, [dispatch]);

  useEffect(() => {
    if (!homeSliderData) {
      getHomeSliderImageApi();
    }
  }, [homeSliderData]);

  return (
    <>
      <HelmetProvider>
        <HelmetComponent metaData={homeMetaData} />
      </HelmetProvider>
      <Header
        mustSeeRef={mustSeeRef}
        exploreMapRef={exploreMapRef}
        whyIndiaRef={whyIndiaRef}
        seasonalTripsRef={seasonalTripsRef}
        reviewRef={reviewRef}
        faqRef={faqRef}
      />
      <HomeSliderComponent homeSliderData={homeSliderData} isLoader={isLoader} />

      <MotionBox
        id="businessCount"
        initial={{ opacity: 0, translateY: 50 }}
        animate={sectionVisibility.businessCount ? { opacity: 1, translateY: 0 } : {}}
        transition={{ duration: 0.1 }}>
        <BusinessCount
          className="business-count"
          businessCount={businessCount}
          isLoader={isLoader}
        />
      </MotionBox>

      <MotionBox
        id="mustSeeSpots"
        initial={{ opacity: 0, translateY: 50 }}
        animate={sectionVisibility.mustSeeSpots ? { opacity: 1, translateY: 0 } : {}}
        transition={{ duration: 0.5 }}>
        <MustSeeSpots mustSeeData={mustSeeData} ref={mustSeeRef} />
      </MotionBox>

      <MotionBox
        id="exploreIndia"
        initial={{ opacity: 0, translateY: 50 }}
        animate={sectionVisibility.exploreIndia ? { opacity: 1, translateY: 0 } : {}}
        transition={{ duration: 0 }}>
        <ExploreIndia ref={exploreMapRef} />
      </MotionBox>

      <Adventures whyIndiaData={whyIndiaData} ref={whyIndiaRef} themeData={themeData} />

      <MotionBox
        id="travelPlanner"
        initial={{ opacity: 0, translateY: 50 }}
        animate={sectionVisibility.travelPlanner ? { opacity: 1, translateY: 0 } : {}}
        transition={{ duration: 0.5 }}>
        <TravelPlanner travelPlannerSlideData={TravelPlannerSlideData} ref={seasonalTripsRef} />
      </MotionBox>
      {/* {faqAccordionData.length > 0 && (
        <MotionBox
          id="faq"
          initial={{ opacity: 0, translateY: 50 }}
          animate={sectionVisibility.faq ? { opacity: 1, translateY: 0 } : {}}
          transition={{ duration: 0.8 }}>
          <Box display="grid" gap="8px" mt="20px" justifyContent="center" mb={6}>
            <Typography
              fontFamily="gothamBold"
              sx={{ pl: { xs: '20px' } }}
              fontSize={{ xs: '18px', lg: '28px' }}
              lineHeight={{ xs: '26px', lg: '62px' }}
              textAlign={{ xs: 'left', lg: 'center' }}>
              {!isLargeScreen ? 'India Frequently Asked Questions' : 'India FAQ'}
            </Typography>
            <Box
              className="accordion-container faq-accordion"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mx: { xs: 2, sm: 4, md: 6, lg: 'auto' },
                mt: { xs: 2, sm: 4, md: 6, lg: 6 },
                mb: { xs: 2, sm: 6, md: 8, lg: 8 },
                px: { xs: 1, sm: 3, md: 4, lg: 5 },
                maxWidth: { xs: '100%', sm: '600px', md: '800px', lg: '1080px' },
                width: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden',
                overflowY: 'auto'
              }}>
              {faqAccordionData.map((data, index) => (
                <FaqComponent
                  key={index}
                  id={index}
                  title={data.title}
                  content={data.content}
                  ref={faqRef}
                />
              ))}
            </Box>
          </Box>
        </MotionBox>
      )} */}
    </>
  );
};

export default Home;
